import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, CreditCard, Bell, Users, Globe, Mail, Loader2, Plane, CalendarClock, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { CompanySettingsModal } from '@/components/settings/company-settings-modal';
import { PaymentSettingsModal } from '@/components/settings/payment-settings-modal';
import { NotificationsSettingsModal } from '@/components/settings/notifications-settings-modal';
import { LocalizationSettingsModal } from '@/components/settings/localization-settings-modal';
import { AmadeusSettingsModal } from '@/components/settings/amadeus-settings-modal';

const settingSections = [
  {
    title: 'Company Settings',
    description: 'Manage your company profile and branding',
    icon: Building2,
    type: 'company' as const,
  },
  {
    title: 'Payment Settings',
    description: 'Configure payment gateways and currencies',
    icon: CreditCard,
    type: 'payment' as const,
  },
  {
    title: 'Notifications',
    description: 'Configure email and notification preferences',
    icon: Bell,
    type: 'notifications' as const,
  },
  {
    title: 'Team Members',
    description: 'Manage staff accounts and roles',
    icon: Users,
    type: 'link' as const,
    href: '/settings/team',
  },
  {
    title: 'User Permissions',
    description: 'Configure app access and module permissions',
    icon: Shield,
    type: 'link' as const,
    href: '/settings/permissions',
  },
  {
    title: 'Localization',
    description: 'Language and regional settings',
    icon: Globe,
    type: 'localization' as const,
  },
  {
    title: 'Email Templates',
    description: 'Customize automated email templates',
    icon: Mail,
    type: 'link' as const,
    href: '/settings/email-templates',
  },
  {
    title: 'Amadeus Settings',
    description: 'Configure flights and hotels markup',
    icon: Plane,
    type: 'amadeus' as const,
  },
  {
    title: 'Payment Plan Templates',
    description: 'Create reusable deposit and installment schedules',
    icon: CalendarClock,
    type: 'link' as const,
    href: '/settings/payment-plan-templates',
  },
];

export function SettingsPage() {
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const { data: quickSettings, isLoading } = trpc.settings.getQuickSettings.useQuery();

  const [defaultCurrency, setDefaultCurrency] = useState<string>('USD');
  const [quoteValidityDays, setQuoteValidityDays] = useState<number>(14);
  const [depositPercentage, setDepositPercentage] = useState<number>(30);

  // Update local state when data loads
  useState(() => {
    if (quickSettings) {
      setDefaultCurrency(quickSettings.defaultCurrency as string);
      setQuoteValidityDays(quickSettings.quoteValidityDays as number);
      setDepositPercentage(quickSettings.depositPercentage as number);
    }
  });

  const utils = trpc.useUtils();

  const updateQuickSettingsMutation = trpc.settings.updateQuickSettings.useMutation({
    onSuccess: () => {
      utils.settings.getQuickSettings.invalidate();
      setIsSaving(false);
    },
    onError: () => {
      setIsSaving(false);
    },
  });

  const handleSaveQuickSettings = () => {
    setIsSaving(true);
    updateQuickSettingsMutation.mutate({
      defaultCurrency,
      quoteValidityDays,
      depositPercentage,
    });
  };

  const handleSectionClick = (section: typeof settingSections[number]) => {
    if (section.type === 'link' && section.href) {
      return; // Let Link handle navigation
    }
    setActiveModal(section.type);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your agency settings and preferences</p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {settingSections.map((section) => {
          const content = (
            <div
              className="group cursor-pointer border border-border bg-card p-6 transition-all hover:shadow-lg"
              onClick={() => handleSectionClick(section)}
            >
              <div className="mb-4 inline-flex bg-primary/10 p-3">
                <section.icon className="h-6 w-6 text-primary" />
              </div>
              <h3 className="font-semibold group-hover:text-primary">{section.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{section.description}</p>
            </div>
          );

          if (section.type === 'link' && section.href) {
            return (
              <Link key={section.title} to={section.href}>
                {content}
              </Link>
            );
          }

          return <div key={section.title}>{content}</div>;
        })}
      </div>

      {/* Quick Settings */}
      <div className="border border-border bg-card p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">Quick Settings</h2>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Default Currency</p>
                <p className="text-xs text-muted-foreground">Currency used for new quotes and invoices</p>
              </div>
              <Combobox
                options={[
                  { value: 'USD', label: 'USD - US Dollar' },
                  { value: 'EUR', label: 'EUR - Euro' },
                  { value: 'GBP', label: 'GBP - British Pound' },
                  { value: 'TRY', label: 'TRY - Turkish Lira' },
                ]}
                value={defaultCurrency}
                onValueChange={setDefaultCurrency}
                className="h-8 w-48"
                placeholder="Select currency"
                searchPlaceholder="Search..."
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Quote Validity</p>
                <p className="text-xs text-muted-foreground">Default validity period for quotes</p>
              </div>
              <Combobox
                options={[
                  { value: '7', label: '7 days' },
                  { value: '14', label: '14 days' },
                  { value: '30', label: '30 days' },
                ]}
                value={String(quoteValidityDays)}
                onValueChange={(v) => setQuoteValidityDays(Number(v))}
                className="h-8 w-48"
                placeholder="Select"
                searchPlaceholder="Search..."
              />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Deposit Percentage</p>
                <p className="text-xs text-muted-foreground">Default deposit required for bookings</p>
              </div>
              <Combobox
                options={[
                  { value: '20', label: '20%' },
                  { value: '30', label: '30%' },
                  { value: '50', label: '50%' },
                ]}
                value={String(depositPercentage)}
                onValueChange={(v) => setDepositPercentage(Number(v))}
                className="h-8 w-48"
                placeholder="Select"
                searchPlaceholder="Search..."
              />
            </div>
          </div>
        )}
        <div className="mt-6 flex justify-end border-t border-border pt-4">
          <Button className="h-8" onClick={handleSaveQuickSettings} disabled={isSaving || isLoading}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </div>
      </div>

      {/* Modals */}
      {activeModal === 'company' && (
        <CompanySettingsModal
          open={true}
          onOpenChange={(open) => !open && setActiveModal(null)}
        />
      )}
      {activeModal === 'payment' && (
        <PaymentSettingsModal
          open={true}
          onOpenChange={(open) => !open && setActiveModal(null)}
        />
      )}
      {activeModal === 'notifications' && (
        <NotificationsSettingsModal
          open={true}
          onOpenChange={(open) => !open && setActiveModal(null)}
        />
      )}
      {activeModal === 'localization' && (
        <LocalizationSettingsModal
          open={true}
          onOpenChange={(open) => !open && setActiveModal(null)}
        />
      )}
      {activeModal === 'amadeus' && (
        <AmadeusSettingsModal
          open={true}
          onOpenChange={(open) => !open && setActiveModal(null)}
        />
      )}
    </div>
  );
}
